import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { toast } from '@/hooks/use-toast';
import { workflow_run_path } from '@/lib/routes';
import type { FormValues } from '@/pages/WorkflowsCatalog/sections/RunSection';
import type { WorkflowDetail } from '@/pages/WorkflowsCatalog/types';
import { router } from '@inertiajs/react';
import { Loader2, Play } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import { Form as ReactHookForm } from 'react-hook-form';

export type RunBaseFormProps = {
  form: UseFormReturn<FormValues>;
  workflow: WorkflowDetail;
} & React.PropsWithChildren;

const RunBaseForm = ({ form, workflow, children }: RunBaseFormProps) => {
  const buttonText =
    form.formState.isSubmitting || form.formState.isLoading ? 'Executing...' : 'Execute Workflow';
  const buttonIcon =
    form.formState.isSubmitting || form.formState.isLoading ? (
      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
    ) : (
      <Play className="mr-2 h-4 w-4" />
    );

  return (
    <Form {...form}>
      <ReactHookForm
        action={`/workflows/${workflow.function_name}/runs?provider=${workflow.provider}`}
        headers={{
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token':
            document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        }}
        onSuccess={async ({ response }) => {
          toast({
            title: 'Success',
            description: 'Workflow execution started successfully',
          });
          const result = await response.json();
          router.visit(
            workflow_run_path(result.workflowId, result.runId, { provider: result.provider })
          );
        }}
        onError={(error) => {
          toast({
            title: 'Error',
            description: error instanceof Error ? error.message : 'Failed to execute workflow',
            variant: 'destructive',
          });
          throw new Error((error as any).error || 'Failed to execute workflow');
        }}
        className="space-y-6"
      >
        <input
          type="hidden"
          {...form.register('workflow_name', { value: workflow.function_name })}
        />

        {children}

        <Button type="submit" disabled={form.formState.isSubmitting || form.formState.isLoading}>
          {buttonIcon}
          {buttonText}
        </Button>
      </ReactHookForm>
    </Form>
  );
};

export default RunBaseForm;
